import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/toast';
import { useMe, useUpdateMe } from '@/features/settings/hooks';
import { env } from '@/lib/env';

export function ProfileForm() {
  const meQuery = useMe();
  const update = useUpdateMe();
  const toast = useToast();

  const [name, setName] = useState('');
  const [crm, setCrm] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [phone, setPhone] = useState('');
  const initialPhone = meQuery.data?.phoneE164 ?? '';

  useEffect(() => {
    if (meQuery.data) {
      setName(meQuery.data.name);
      setCrm(meQuery.data.crm ?? '');
      setSpecialty(meQuery.data.specialty ?? '');
      setPhone(meQuery.data.phoneE164 ?? '');
    }
  }, [meQuery.data]);

  const phoneChanged = phone !== initialPhone && phone.length > 0;

  const inboundEmail = meQuery.data?.inboundAlias
    ? `${meQuery.data.inboundAlias}@${env.inboundEmailDomain}`
    : '';

  const handleCopyInboundEmail = async () => {
    if (!inboundEmail) return;
    try {
      await navigator.clipboard.writeText(inboundEmail);
      toast.show({ title: 'E-mail copiado.', variant: 'success' });
    } catch {
      toast.show({
        title: 'Não foi possível copiar',
        description: 'Selecione e copie manualmente.',
        variant: 'error',
      });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        name,
        crm,
        specialty,
        phoneE164: phone || undefined,
      });
      if (phoneChanged) {
        toast.show({
          title: 'Telefone atualizado.',
          description: 'Você precisará verificar o novo número por OTP.',
          variant: 'success',
        });
      } else {
        toast.show({ title: 'Perfil salvo.', variant: 'success' });
      }
    } catch (err) {
      toast.show({
        title: 'Erro ao salvar',
        description: err instanceof Error ? err.message : 'Tente novamente.',
        variant: 'error',
      });
    }
  };

  if (meQuery.isLoading) {
    return <p className="text-sm text-(--color-fg-muted)">Carregando perfil…</p>;
  }

  return (
    <Card>
      <form onSubmit={handleSave} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="name">Nome completo</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="crm">CRM</Label>
          <Input
            id="crm"
            value={crm}
            onChange={(e) => setCrm(e.target.value)}
            placeholder="CRM/SP 123456"
            maxLength={20}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="specialty">Especialidade</Label>
          <Input
            id="specialty"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            placeholder="Ex.: Anestesiologia"
            maxLength={80}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="phone">WhatsApp (E.164)</Label>
          <Input
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+5511988887777"
            pattern="^\+\d{8,19}$"
          />
          {phoneChanged && (
            <p className="text-xs text-(--color-warning)">
              Alterar o telefone exige nova verificação por OTP.
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="inboundEmail">E-mail para receber notas fiscais</Label>
          <div className="flex gap-2">
            <Input
              id="inboundEmail"
              readOnly
              value={inboundEmail}
              placeholder="—"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={handleCopyInboundEmail}
              disabled={!inboundEmail}
            >
              Copiar
            </Button>
          </div>
          <p className="text-xs text-(--color-fg-muted)">
            Compartilhe este endereço com seus hospitais. Notas enviadas para cá
            são processadas automaticamente.
          </p>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? 'Salvando…' : 'Salvar alterações'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
